import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { UsersSelectors } from '../../../Redux/modules/users/selectors';
import styles from './UserContainer.module.less';
import Users from '../components/Users';
import Tabs from '../../Tabs/Tabs';
import { ProfileSelectors } from '../../../Redux/modules/profile/selectors';
import { AppSelectors } from '../../../Redux/modules/app/selectors';
import { UsersActions } from '../../../Redux/modules/users/actions';
import AppConfig from '../../../Config/AppConfig';
import { ColumnHeaders, InviteMapper, StatusFilterMapper } from '../constants';
import AnalyticsCore from '../../../Utils/AnalyticsHandler/AnalyticsCore';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import InsightsParagraph from '../components/InsightsParagraph';

const UsersContainer = (props) => {
  const actions = [
    {
      id: 0,
      name: 'EDIT_PROFILE',
      title: gettext('Update name'),
      onClick: () => {
        props.clickedEditUserButton();
      },
      permission: () => {
        return (
          props.currentUser?.status?.type === StatusFilterMapper.ACTIVE &&
          props.currentUser?.is_active &&
          props.currentLoggedEmail !== props.currentUser?.email
        );
      },
    },
    {
      id: 1,
      name: 'RESET_PASSWORD',
      title: gettext('Reset password'),
      onClick: () => {
        props.clickedResetPasswordButton();
      },
      permission: () => {
        return (
          props.currentUser?.status?.type === StatusFilterMapper.ACTIVE &&
          props.currentUser?.is_active &&
          props.currentLoggedEmail !== props.currentUser?.email
        );
      },
    },
    {
      id: 2,
      name: 'ASSIGN_TO_TEAMS',
      title: gettext('Assign teams'),
      onClick: () => {
        props.clickedAssignToTeamsButton();
      },
      permission: () => {
        return props.isTeamManagement && (props.teams || []).length > 0;
      },
    },
    {
      id: 3,
      name: 'UPDATE_PERMISSIONS',
      title: gettext('Assign products'),
      onClick: () => {
        props.clickedUpdatePermissionsButton();
      },
      permission: () => {
        return props.currentUser?.status?.type === StatusFilterMapper.ACTIVE;
      },
    },
    {
      id: 4,
      name: 'RESEND_INVITE',
      title: gettext('Resend invite'),
      onClick: () => {
        props.clickedResendInviteButton();
      },
      permission: () => {
        return (
          props.currentUser?.status?.invite_status === InviteMapper.INVITE_SENT ||
          props.currentUser?.status?.type === StatusFilterMapper.PENDING
        );
      },
    },
    {
      id: 5,
      name: 'REMOVE_INVITE',
      title: gettext('Remove user'),
      style: { color: '#d7446f' },
      onClick: () => {
        props.clickedRemoveInviteButton();
      },
      permission: () => {
        return props.currentUser?.status?.invite_status === InviteMapper.INVITE_SENT;
      },
    },
    {
      id: 6,
      name: 'REMOVE_USER',
      title: gettext('Remove user'),
      style: { color: '#d7446f' },
      onClick: () => {
        props.clickedRemoveUserButton();
      },
      permission: () => {
        return (
          props.currentUser?.status?.type !== StatusFilterMapper.INVITE &&
          props.currentLoggedEmail !== props.currentUser?.email
        );
      },
    },
    {
      id: 7,
      name: 'COPY_ACTIVATION_LINK',
      title: gettext('Copy activation link'),
      onClick: () => {
        props.copyActivationLink(props.currentUser.id);
        if (props.tracking.isEnabled) {
          const trackingData = AnalyticsCore.buildEvent({
            user: props.user,
            tracking: props.tracking,
            eventName: AnalyticsEventNames.COPY_ACTIVATION_LINK,
            eventData: { manipulatedUser: props.currentUser.id },
          });

          AnalyticsCore.sendEvent(trackingData);
        }
      },
      permission: () => {
        return (
          AppConfig.ACTIVATION_LINKS_ENABLED &&
          props.currentUser?.status?.type !== StatusFilterMapper.INVITE &&
          !props.currentUser?.is_active
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>Users and teams</div>
      {props.usersData.search_status && !props.hasApiContext && (
        <InsightsParagraph
          availableSubscriptions={props.availableSubscriptions}
          totalSubscriptions={props.totalSubscriptions}
          isTrial={props.isTrial}
        />
      )}
      <Tabs
        tabs={[
          { title: gettext(ColumnHeaders.USERS), link: 'manage-users/users/' },
          { title: gettext(ColumnHeaders.TEAMS), link: 'manage-users/teams/' },
        ]}
      />
      <div className={styles.section}>
        <div className={styles.sectionDiv}>
          <Users
            totalUsers={props.totalUsers}
            totalAdmins={props.totalAdmins}
            users={props.users}
            fetchUsers={props.onShowUsers}
            usersData={props.usersData}
            clickedEditUserButton={props.clickedEditUserButton}
            actions={actions}
            availableSubscriptions={props.availableSubscriptions}
            totalSubscriptions={props.totalSubscriptions}
            isTrial={props.isTrial}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    totalUsers: UsersSelectors.totalUserCountSelector(state),
    totalAdmins: UsersSelectors.totalAdminCountSelector(state),
    users: UsersSelectors.orgUsersSelector(state),
    usersData: UsersSelectors.usersDataSelector(state),
    currentLoggedEmail: ProfileSelectors.profileEmailSelector(state),
    currentUser: UsersSelectors.currentClickedUserSelector(state),
    editProfileModalOpen: UsersSelectors.updateProfileModalSelector(state),
    resetPasswordModalOpen: UsersSelectors.resetPasswordModalSelector(state),
    removeUserModalOpen: UsersSelectors.removeUserModalSelector(state),
    resendInviteModalOpen: UsersSelectors.resendInviteModalSelector(state),
    removeInviteModalOpen: UsersSelectors.removeInviteModalSelector(state),
    updatePermissionsModalOpen: UsersSelectors.updatePermissionsModalSelector(state),
    assignToTeamsModalOpen: UsersSelectors.assignToTeamsModalSelector(state),
    modalError: UsersSelectors.modalErrorSelector(state),
    modalErrorMessage: UsersSelectors.modalErrorMessageSelector(state),
    teams: UsersSelectors.teamsSelector(state),
    isTeamManagement: AppSelectors.isTeamManagementSelector(state),
    user: AppSelectors.userSelector(state),
    tracking: AppSelectors.trackingSelector(state),
    availableSubscriptions: UsersSelectors.availableSubscriptionsSelector(state),
    totalSubscriptions: UsersSelectors.totalSubscriptionSelector(state),
    isTrial: UsersSelectors.isTrialSelector(state),
    hasApiContext: AppSelectors.hasApiContextSelector(state),
  };
};
const mapDispatchToProps = {
  clickedEditUserButton: UsersActions.clickedEditUserButton,
  clickedCloseModalButton: UsersActions.clickedCloseModalButton,
  clickedResetPasswordButton: UsersActions.clickedResetPasswordButton,
  clickedRemoveUserButton: UsersActions.clickedRemoveUserButton,
  clickedResendInviteButton: UsersActions.clickedResendInviteButton,
  clickedRemoveInviteButton: UsersActions.clickedRemoveInviteButton,
  clickedUpdatePermissionsButton: UsersActions.clickedUpdatePermissionsButton,
  clickedAssignToTeamsButton: UsersActions.clickedAssignToTeamsButton,
  copyActivationLink: UsersActions.copyActivationLink,
};

UsersContainer.propTypes = {
  totalUsers: PropTypes.number,
  totalAdmins: PropTypes.number,
  users: PropTypes.array,
  onShowUsers: PropTypes.func,
  usersData: PropTypes.object,
  clickedEditUserButton: PropTypes.func,
  clickedResetPasswordButton: PropTypes.func,
  clickedRemoveUserButton: PropTypes.func,
  clickedResendInviteButton: PropTypes.func,
  clickedRemoveInviteButton: PropTypes.func,
  clickedUpdatePermissionsButton: PropTypes.func,
  clickedAssignToTeamsButton: PropTypes.func,
  copyActivationLink: PropTypes.func,
  currentUser: PropTypes.object,
  currentLoggedEmail: PropTypes.string,
  isTeamManagement: PropTypes.bool,
  teams: PropTypes.array,
  user: PropTypes.object,
  tracking: PropTypes.object,
  availableSubscriptions: PropTypes.number,
  totalSubscriptions: PropTypes.number,
  isTrial: PropTypes.bool,
  hasApiContext: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersContainer);
